import React from "react";
import { graphql } from "react-relay";

import cx from "classnames";
import invariant from "tiny-invariant";

import { BillingContext } from "~/account/Billing/BillingContext";
import { useDebouncedValue, useID } from "~/lib/hooks";
import { warning } from "~/lib/prompts";
import { useMutationPromise } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { getFirstUserFacingError } from "~/lib/utils";
import { eventEmitter as recordingsEventEmitter } from "~/recordings/events";
import { Button } from "~/ui/button";
import { Input } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import { PreviewThumbnail } from "../PreviewThumbnail";
import { openShareClipModal } from "../ShareClipModal";
import { formatTimestamp, parseHumanReadableTimestamp } from "../utils";
import { ControlTooltip } from "../Video";

import type { ClipTrimmer_VisibilityChangeMutation as VisibilityChangeMutation } from "~/__generated__/ClipTrimmer_VisibilityChangeMutation.graphql";
import type { ClipTrimmerSaveClipMutation } from "~/__generated__/ClipTrimmerSaveClipMutation.graphql";

import styles from "./css/ClipTrimmer.module.css";

function useElementWidth(element: HTMLElement | null) {
	const [width, setWidth] = React.useState<number | undefined>(undefined);

	React.useLayoutEffect(() => {
		setWidth(element?.clientWidth);
	}, [element]);

	React.useEffect(() => {
		const resizeObserver = new ResizeObserver(entries => {
			for (const entry of entries) {
				if (entry.target === element) {
					setWidth(entry.target.clientWidth);
				}
			}
		});

		if (element) resizeObserver.observe(element);

		return () => resizeObserver.disconnect();
	}, [element]);

	return width;
}

interface TimelineProps {
	thumbnailTrack: TextTrack;
	videoDuration: number;
	containerWidth: number;
	initialVideoPosition: number;
	initialVideoEndPosition?: number;
	videoRef: React.RefObject<HTMLVideoElement>;
	onClipStartChange: (start: number) => void;
	onClipEndChange: (end: number) => void;
}

// we're using a 'half-width' thumbnail where we only show the centre of the thumbnail, half the width of the full thumbnail
const THUMBNAIL_WIDTH = 48;
const THUMBNAIL_HEIGHT = 54;
const MIN_CLIP_LENGTH = 3; // seconds
const HANDLE_WIDTH = 10;
const TIME_TOOLTIP_OFFSET: [number, number] = [0, 8];

export const MIN_VIDEO_DURATION_FOR_CLIPPING = 120;

interface TimelineState {
	/** The pixel position of the left clip handle. */
	clipStartPos: number;
	/** The pixel position of the right clip handle. */
	clipEndPos: number;
	/** The pixel position of the current time indicator. */
	currentTimeIndicatorPos: number;
}

type TimelineAction =
	| { type: "move handle"; side: "left" | "right"; pos: [number, number] }
	| { type: "move current time indicator"; pos: number }
	| { type: "video time update"; time: number }
	| { type: "container resize"; containerWidth: number; prevContainerWidth: number };

type DraggingState = (
	| { type: "handle"; side: "left" | "right"; offsetX: number }
	| { type: "current time indicator" }
) & {
	pointerId: number;
};

function pixelsPerSecondImpl(containerWidth: number, videoDuration: number) {
	// 2 HANDLE_WIDTHs are subtracted from the container width to allow for
	// selecting the entire video. For example, when the left handle is at pixel
	// position 0, the right side of it is positioned at the 0th second.
	return (containerWidth - 2 * HANDLE_WIDTH) / videoDuration;
}

function timeToPixelsImpl(time: number, containerWidth: number, videoDuration: number) {
	// All times are offset by HANDLE_WIDTH pixels to allow for selecting to the
	// start of the video.
	return time * pixelsPerSecondImpl(containerWidth, videoDuration) + HANDLE_WIDTH;
}

function pixelsToTimeImpl(pixels: number, containerWidth: number, videoDuration: number) {
	// HANDLE_WIDTH is subtracted from pixels because it's assumed that pixels is
	// offset as described above.
	return Math.round((pixels - HANDLE_WIDTH) / pixelsPerSecondImpl(containerWidth, videoDuration));
}

/**
 * Figure out the distance between the left edge of contextEl and the pointer.
 */
function relativeLeftOffset(event: MouseEvent, contextEl: HTMLElement) {
	return event.clientX - contextEl.getBoundingClientRect().left;
}

interface ClippingTimelineRef {
	setClipStart: (start: number, end: number) => void;
	setClipEnd: (start: number, end: number) => void;
}

const ClippingTimeline = React.forwardRef<ClippingTimelineRef, TimelineProps>(function ClippingTimeline(
	{
		thumbnailTrack,
		videoDuration,
		initialVideoPosition,
		initialVideoEndPosition,
		containerWidth,
		videoRef,
		onClipStartChange,
		onClipEndChange,
	},
	ref,
) {
	const numThumbnails = Math.ceil(containerWidth / THUMBNAIL_WIDTH);

	const [dragging, setDragging] = React.useState<DraggingState | undefined>(undefined);

	const pixelsToTime = React.useCallback(
		(p: number) => pixelsToTimeImpl(p, containerWidth, videoDuration),
		[containerWidth, videoDuration],
	);
	const timeToPixels = React.useCallback(
		(t: number) => timeToPixelsImpl(t, containerWidth, videoDuration),
		[containerWidth, videoDuration],
	);

	// If the current video position is too close to the start or end of the
	// video, the selected area will be offset accordingly.
	const initialSelectedRangeStartTime = Math.min(Math.max(initialVideoPosition, 0), videoDuration - MIN_CLIP_LENGTH),
		initialSelectedRangeEndTime = Math.min(initialVideoEndPosition || initialVideoPosition + 60, videoDuration);

	const initialState = {
		clipStartPos: timeToPixels(initialSelectedRangeStartTime),
		clipEndPos: timeToPixels(initialSelectedRangeEndTime),
		// The current time indicator is initially placed at the start of the
		// selected range.
		currentTimeIndicatorPos: timeToPixels(initialVideoPosition),
	};

	// Expose methods to set the start and end of the clip outside the component.
	// This is how we can programmatically set the clip start and end times using
	// the input fields below the clipping timeline.
	React.useImperativeHandle(
		ref,
		() => ({
			setClipStart: (start: number, end: number) => {
				dispatch({ type: "move handle", side: "left", pos: [timeToPixels(start), timeToPixels(end)] });
			},
			setClipEnd: (start: number, end: number) => {
				dispatch({ type: "move handle", side: "right", pos: [timeToPixels(start), timeToPixels(end)] });
			},
		}),
		[timeToPixels],
	);

	const [{ clipStartPos, clipEndPos, currentTimeIndicatorPos }, dispatch] = React.useReducer(
		function timelineStateReducer(state: TimelineState, action: TimelineAction): TimelineState {
			switch (action.type) {
				// When the user moves the left or right handle.
				case "move handle": {
					// The pos in the action is relative to the left side of the time
					// range, so we'll make it an absolute position relative to the start
					// of the video (in pixels according to the pixels:time ratio)

					// We need the current value of both ends of the clip positions in the
					// action data so that we can handle the case where the user tries to
					// set the start time after the end time or vice versa. Since we expose
					// input fields in ClippingControls that allow the user to set the start
					// and end times directly, they could pass invalid times and we need to
					// adjust the timeline when those times do become valid from a change
					// to either input value
					const desiredStartPos = Math.max(0, Math.min(action.pos[0], containerWidth));
					const desiredEndPos = Math.max(0, Math.min(action.pos[1], containerWidth));

					if (action.side === "left") {
						// The handle needs to be at minimum one handle width away from the
						// left side of the timeline...
						const newStart = Math.max(
							HANDLE_WIDTH,
							// ...and at most MIN_CLIP_LENGTH before the right handle
							// The HANDLE_WIDTH is added to the minimum clip position
							// to account for the right handle position
							Math.min(desiredStartPos, desiredEndPos - timeToPixels(MIN_CLIP_LENGTH) + HANDLE_WIDTH),
						);
						videoRef.current && (videoRef.current.currentTime = pixelsToTime(newStart));

						return {
							...state,
							clipStartPos: newStart,
							currentTimeIndicatorPos: newStart,
						};
					} else {
						// The handle needs to be at maximum one handle width away from the
						// right side of the timeline...
						const newEnd = Math.min(
							timeToPixels(videoDuration),
							// ...and at least MIN_CLIP_LENGTH after the left handle
							// The HANDLE_WIDTH is subtracted from the minimum clip position
							// to account for the left handle position
							Math.max(desiredEndPos, desiredStartPos + timeToPixels(MIN_CLIP_LENGTH) - HANDLE_WIDTH),
						);
						videoRef.current && (videoRef.current.currentTime = pixelsToTime(newEnd));
						return {
							...state,
							clipEndPos: newEnd,
							currentTimeIndicatorPos: newEnd,
						};
					}
				}

				// When the user moves the current time indicator.
				case "move current time indicator": {
					let { pos } = action;
					// Ensure the new pos is within the timeline and the selected range.
					pos = Math.max(Math.min(pos, containerWidth), 0);
					pos = Math.max(Math.min(pos, state.clipEndPos), state.clipStartPos);
					if (videoRef.current) videoRef.current.currentTime = pixelsToTime(pos);
					return {
						...state,
						currentTimeIndicatorPos: pos,
					};
				}

				case "video time update": {
					let { time } = action;
					const selectedTimeStart = pixelsToTime(state.clipStartPos),
						selectedTimeEnd = pixelsToTime(state.clipEndPos);
					if (time < selectedTimeStart) {
						time = selectedTimeStart;
					} else if (time > selectedTimeEnd) {
						const draggingRightHandle = dragging && dragging.type === "handle" && dragging.side === "right";
						time = draggingRightHandle ? selectedTimeEnd : selectedTimeStart;
						if (draggingRightHandle) {
							videoRef.current?.pause();
						} else if (videoRef.current) {
							videoRef.current.currentTime = selectedTimeStart;
						}
					}
					return {
						...state,
						currentTimeIndicatorPos: timeToPixels(time),
					};
				}

				case "container resize": {
					const { containerWidth, prevContainerWidth } = action;

					function mapPosToNewSize(oldValue: number) {
						return timeToPixelsImpl(
							pixelsToTimeImpl(oldValue, prevContainerWidth, videoDuration),
							containerWidth,
							videoDuration,
						);
					}

					return {
						...state,
						clipStartPos: mapPosToNewSize(state.clipStartPos),
						clipEndPos: mapPosToNewSize(state.clipEndPos),
						currentTimeIndicatorPos: mapPosToNewSize(state.currentTimeIndicatorPos),
					};
				}
			}
		},
		initialState,
	);

	const leftHandlePos = clipStartPos - HANDLE_WIDTH,
		rightHandlePos = clipEndPos,
		currentTime = pixelsToTime(currentTimeIndicatorPos);

	React.useEffect(() => {
		onClipStartChange(pixelsToTime(clipStartPos));
	}, [onClipStartChange, pixelsToTime, clipStartPos]);

	React.useEffect(() => {
		onClipEndChange(pixelsToTime(clipEndPos));
	}, [onClipEndChange, pixelsToTime, clipEndPos]);

	const prevContainerSizeRef = React.useRef(containerWidth);
	React.useEffect(() => {
		if (containerWidth !== prevContainerSizeRef.current) {
			dispatch({
				type: "container resize",
				containerWidth,
				prevContainerWidth: prevContainerSizeRef.current,
			});
			prevContainerSizeRef.current = containerWidth;
		}
	}, [containerWidth]);

	function onPointerDownInLeftHandle(event: React.PointerEvent) {
		event.preventDefault();
		setDragging({
			pointerId: event.pointerId,
			type: "handle",
			side: "left",
			// The offset of the pointer within the drag handle.
			offsetX: event.nativeEvent.offsetX,
		});
		window.addEventListener("pointermove", onPointerMove);
		window.addEventListener("pointerup", onPointerUp);
	}

	function onPointerDownInRightHandle(event: React.PointerEvent) {
		event.preventDefault();
		setDragging({
			pointerId: event.pointerId,
			type: "handle",
			side: "right",
			offsetX: event.nativeEvent.offsetX,
		});
		window.addEventListener("pointermove", onPointerMove);
		window.addEventListener("pointerup", onPointerUp);
	}

	function onPointerDownInSelectedRange(event: React.PointerEvent) {
		event.preventDefault();
		setDragging({
			pointerId: event.pointerId,
			type: "current time indicator",
		});
		window.addEventListener("pointermove", onPointerMove);
		window.addEventListener("pointerup", onPointerUp);
	}

	function onPointerUp() {
		setDragging(undefined);
		window.removeEventListener("pointermove", onPointerMove);
		window.removeEventListener("pointerup", onPointerUp);
	}

	function onPointerMove(event: PointerEvent) {
		if (!draggingRef.current) {
			return;
		}

		if (!timelineRef.current) return;
		const pos = relativeLeftOffset(event, timelineRef.current);

		if (draggingRef.current.type === "handle") {
			const { side, offsetX } = draggingRef.current;
			if (side === "left") {
				// Add/subtract the offset of the cursor in the drag handle so the
				// handle stays in the same position relative to your pointer as you
				// drag it.
				dispatch({ type: "move handle", side, pos: [pos + offsetX, clipEndPos] });
			} else if (side === "right") {
				dispatch({ type: "move handle", side, pos: [clipStartPos, pos - offsetX] });
			}
		} else {
			dispatch({ type: "move current time indicator", pos });
		}
	}

	const timelineRef = React.useRef<HTMLDivElement>(null);

	// Move the video's current time to where you click on the selected range.
	function onSelectedRangeClick(event: React.MouseEvent) {
		if (!timelineRef.current) return;
		const pos = relativeLeftOffset(event.nativeEvent, timelineRef.current);
		dispatch({ type: "move current time indicator", pos });
	}

	// Store the current value of dragging in a ref so that it can be used in
	// event listeners without having to rerun the effect that adds the listener.
	const draggingRef = React.useRef(dragging);
	draggingRef.current = dragging;

	function* thumbnailElements() {
		if (!numThumbnails) return;

		for (let i = 0; i < numThumbnails; i++) {
			const startTime = pixelsToTime(HANDLE_WIDTH + i * THUMBNAIL_WIDTH);
			// FIXME: Handle videos without any thumbnails? Or we need to generate them
			// for every existing video
			if (!thumbnailTrack.cues) {
				continue;
			}

			const cue = [...thumbnailTrack.cues].find(cue => cue && cue.startTime <= startTime && startTime < cue.endTime);
			invariant(cue instanceof VTTCue || cue === undefined);
			const thumbnailSrc = cue?.text;

			yield (
				<PreviewThumbnail
					key={startTime}
					className={styles.previewThumbnail}
					thumbnailSrc={thumbnailSrc}
					width={THUMBNAIL_WIDTH}
					height={THUMBNAIL_HEIGHT}
					halfsize
				/>
			);
		}
	}

	const selectedTimeStart = pixelsToTime(clipStartPos),
		selectedTimeEnd = pixelsToTime(clipEndPos);

	React.useEffect(() => {
		function onTimeUpdate() {
			const time = videoRef.current?.currentTime;
			if (time === undefined) return;
			if (draggingRef.current?.type === "current time indicator") return;
			dispatch({ type: "video time update", time });
		}

		const video = videoRef.current;
		video?.addEventListener("timeupdate", onTimeUpdate);
		return () => video?.removeEventListener("timeupdate", onTimeUpdate);
	}, [videoRef]);

	return (
		<div ref={timelineRef} className={styles.timeline}>
			{[...thumbnailElements()]}
			{clipStartPos >= 0 && (
				<div
					className={styles.dimmer}
					style={{
						// Add HANDLE_WIDTH so the dimmer also covers the area behind the
						// radiused corners of the handle.
						width: leftHandlePos + HANDLE_WIDTH,
					}}
				/>
			)}
			<ControlTooltip
				content={formatTimestamp(selectedTimeStart, videoDuration)}
				offset={TIME_TOOLTIP_OFFSET}
				placement="bottom-start"
				className={styles.clipTimeTooltip}
			>
				<div
					className={cx(styles.handle, styles.leftHandle)}
					style={{ left: leftHandlePos, width: HANDLE_WIDTH }}
					onPointerDown={onPointerDownInLeftHandle}
				/>
			</ControlTooltip>
			<div
				className={styles.selectedBorder}
				style={{
					left: Math.max(leftHandlePos + HANDLE_WIDTH, 0),
					width: Math.min(rightHandlePos - (leftHandlePos + HANDLE_WIDTH), containerWidth),
				}}
				onPointerDown={onPointerDownInSelectedRange}
				onClick={onSelectedRangeClick}
			>
				<ControlTooltip
					content={formatTimestamp(currentTime, videoDuration)}
					offset={TIME_TOOLTIP_OFFSET}
					placement="bottom"
				>
					<div
						className={styles.currentTimeIndicator}
						style={{
							left: `${currentTimeIndicatorPos - Math.max(clipStartPos, 0) - 1}px`,
						}}
					/>
				</ControlTooltip>
			</div>
			<ControlTooltip
				content={formatTimestamp(selectedTimeEnd, videoDuration)}
				offset={TIME_TOOLTIP_OFFSET}
				placement="bottom-end"
				className={styles.clipTimeTooltip}
			>
				<div
					className={cx(styles.handle, styles.rightHandle)}
					style={{ left: rightHandlePos, width: HANDLE_WIDTH }}
					onPointerDown={onPointerDownInRightHandle}
				/>
			</ControlTooltip>
			{clipEndPos <= videoDuration + HANDLE_WIDTH && (
				<div
					className={styles.dimmer}
					style={{
						// Position the dimmer at the same point as the right handle so
						// the area behind the radiused corners is also dimmed.
						left: rightHandlePos,
						width: containerWidth - rightHandlePos,
					}}
				/>
			)}
		</div>
	);
});

function useSaveClipMutation() {
	const [commit, isInFlight] = useMutationPromise<ClipTrimmerSaveClipMutation>(graphql`
		mutation ClipTrimmerSaveClipMutation($recordingId: ID!, $startTime: Float!, $endTime: Float!, $title: String) {
			createClip(recordingId: $recordingId, startTime: $startTime, endTime: $endTime, title: $title) {
				clip {
					id
					clipStart
					clipEnd
					...ClipsPanel_ClipSummary_clip
				}
				meetingRecording {
					...ClipsPanel_meetingRecording
				}
			}
		}
	`);

	const saveClip = React.useCallback(
		async (recordingId: string, startTime: number, endTime: number, title: string) => {
			return await commit({ variables: { recordingId, startTime, endTime, title } });
		},
		[commit],
	);

	return [saveClip, isInFlight] as const;
}

export async function confirmDiscardClip() {
	const keep = await warning(<>If you do, you&apos;ll lose all your changes.</>, {
		noText: "Keep",
		yesText: "Discard clip",
		title: "Discard this clip?",
		noCloseButton: true,
		noHeaderBorder: true,
		className: styles.confirmDialog,
	});

	return keep;
}

export function ClippingControls({
	recordingId,
	videoRef,
	exitClipMode,
	noteTitle,
	onClipSaveChangeTab,
	videoDuration,
	fromNote,
	initialTimestamp,
	initialEndTimestamp,
}: {
	recordingId: string;
	videoRef: React.RefObject<HTMLVideoElement>;
	exitClipMode: () => void;
	noteTitle: string;
	onClipSaveChangeTab: () => void;
	videoDuration: number;
	fromNote?: boolean;
	initialTimestamp?: number;
	initialEndTimestamp?: number;
}) {
	const [thumbnailTrack, setThumbnailTrack] = React.useState<TextTrack | undefined>();

	const { analyticsFingerprint } = React.useContext(BillingContext);

	React.useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		function onLoadedMetadata() {
			if (!video) return;

			const thumbnailTrack = [...video.textTracks].find(
				track => track.kind === "metadata" && track.label === "thumbnails",
			);

			if (thumbnailTrack instanceof TextTrack) setThumbnailTrack(thumbnailTrack);
		}

		onLoadedMetadata();

		video.addEventListener("loadedmetadata", onLoadedMetadata);
		return () => void video.removeEventListener("loadedmetadata", onLoadedMetadata);
	}, [videoRef]);

	const [timelineEl, setTimelineEl] = React.useState<HTMLDivElement | null>(null);
	const containerWidth = useElementWidth(timelineEl);
	const timelineRef = React.useRef<ClippingTimelineRef>(null);

	const [clipStartTime, setClipStartTime] = React.useState<number | undefined>(initialTimestamp);
	const [clipEndTime, setClipEndTime] = React.useState<number | undefined>(initialEndTimestamp);
	const [clipName, setClipName] = React.useState(`Clip of ${noteTitle}`);

	/**  Clip Inputs */
	// The iniital value of "onload" is being used to determine our first render(s).
	// Whenever the input fields change, we validate the values before trying to
	// save them (onSave function). But on the first render we don't want to validate
	// the input fields since they need to be populated first by the two useEffects below.
	// By checking if the input fields are "onload" we can avoid validating them on the
	// first render.
	const [clipStartInput, setClipStartInput] = React.useState(
		initialTimestamp && initialEndTimestamp ? formatTimestamp(initialTimestamp, videoDuration) : "onload",
	);
	const [clipEndInput, setClipEndInput] = React.useState(
		initialTimestamp && initialEndTimestamp ? formatTimestamp(initialEndTimestamp, videoDuration) : "onload",
	);
	const [startInputError, setStartInputError] = React.useState<string | null>(null);
	const [endInputError, setEndInputError] = React.useState<string | null>(null);

	// Update the start input field when we receive a change/update from the left clip handle
	React.useEffect(() => {
		if (clipStartTime === undefined) return;
		setClipStartInput(formatTimestamp(clipStartTime, videoDuration));
		setStartInputError(null);
	}, [clipStartTime, videoDuration]);

	// Update the end input field when we receive a change/update from the right clip handle
	React.useEffect(() => {
		if (clipEndTime === undefined) return;
		setClipEndInput(formatTimestamp(clipEndTime, videoDuration));
		setEndInputError(null);
	}, [clipEndTime, videoDuration]);

	const onStartInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setClipStartInput(event.currentTarget.value);
		setStartInputError(null);
	}, []);

	const onEndInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setClipEndInput(event.currentTarget.value);
		setEndInputError(null);
	}, []);

	const onNameInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setClipName(event.currentTarget.value);
	}, []);

	const debouncedStartInput = useDebouncedValue(clipStartInput, 750);
	const debouncedEndInput = useDebouncedValue(clipEndInput, 750);

	const validateStartTime = React.useCallback(
		(start: string, end: string) => {
			let errorMessage: string | null = null;
			try {
				const newStartTime = parseHumanReadableTimestamp(start);
				const currentEndTime = parseHumanReadableTimestamp(end);

				if (newStartTime < 0 || newStartTime >= videoDuration) {
					errorMessage = "Timestamp is out of range";
				} else if (newStartTime >= currentEndTime) {
					errorMessage = "Start time must be before end time";
				} else if (currentEndTime - newStartTime < MIN_CLIP_LENGTH) {
					errorMessage = `Clip must be at least ${MIN_CLIP_LENGTH} seconds long`;
				}
			} catch (e) {
				errorMessage = "Invalid timestamp format";
			}
			setStartInputError(errorMessage);
			return errorMessage === null;
		},
		[videoDuration],
	);

	const validateEndTime = React.useCallback(
		(start: string, end: string) => {
			let errorMessage: string | null = null;
			try {
				const newEndTime = parseHumanReadableTimestamp(end);
				const currentStartTime = parseHumanReadableTimestamp(start);

				if (newEndTime < 0 || newEndTime >= videoDuration) {
					errorMessage = "Timestamp is out of range";
				} else if (newEndTime <= currentStartTime) {
					errorMessage = "End time must be after start time";
				} else if (newEndTime - currentStartTime < MIN_CLIP_LENGTH) {
					errorMessage = `Clip must be at least ${MIN_CLIP_LENGTH} seconds long`;
				}
			} catch (e) {
				errorMessage = "Invalid timestamp format";
			}
			setEndInputError(errorMessage);
			return errorMessage === null;
		},
		[videoDuration],
	);

	const updateClipStart = React.useCallback(() => {
		if (!timelineRef.current) return;

		const newStartTime = parseHumanReadableTimestamp(clipStartInput);
		const newEndTime = parseHumanReadableTimestamp(clipEndInput);
		timelineRef.current?.setClipStart(newStartTime, newEndTime);
		setStartInputError(null);
	}, [clipStartInput, clipEndInput]);

	const updateClipEnd = React.useCallback(() => {
		if (!timelineRef.current) return;

		const newStartTime = parseHumanReadableTimestamp(clipStartInput);
		const newEndTime = parseHumanReadableTimestamp(clipEndInput);
		timelineRef.current?.setClipEnd(newStartTime, newEndTime);
		setEndInputError(null);
	}, [clipStartInput, clipEndInput]);

	const onSave = React.useCallback(
		(start: string, end: string) => {
			if (!clipStartTime || !clipEndTime) return;

			// Check if our input field values match the current clip start and end times stored in state
			const doesStartMatch = start === formatTimestamp(clipStartTime, videoDuration);
			const doesEndMatch = end === formatTimestamp(clipEndTime, videoDuration);

			if (!doesStartMatch) {
				const isStartValid = validateStartTime(start, end);
				if (isStartValid) updateClipStart();
			}

			if (!doesEndMatch) {
				const isEndValid = validateEndTime(start, end);
				if (isEndValid) updateClipEnd();
			}
		},
		[clipEndTime, clipStartTime, videoDuration, updateClipEnd, updateClipStart, validateEndTime, validateStartTime],
	);

	React.useEffect(() => {
		// If the input fields are "onload" we don't want to validate them since we haven't set the initial values yet
		if (debouncedStartInput === "onload" || debouncedEndInput === "onload") return;
		onSave(debouncedStartInput, debouncedEndInput);
	}, [debouncedEndInput, debouncedStartInput]); // eslint-disable-line react-hooks/exhaustive-deps

	const onCancel = React.useCallback(async () => {
		if (await confirmDiscardClip()) {
			dispatchAnalyticsTrack("[AI Copilot] Cancelled Clipping", { fingerprint: analyticsFingerprint });
			exitClipMode();
		}
	}, [analyticsFingerprint, exitClipMode]);

	const [saveClip, saving] = useSaveClipMutation();
	const onDone = React.useCallback(async () => {
		if (clipStartTime === undefined || clipEndTime === undefined) return;

		let clipId: string;
		try {
			const result = await saveClip(recordingId, clipStartTime, clipEndTime, clipName);
			if (!result.createClip) throw new Error("Failed to save clip");

			clipId = result.createClip.clip.id;
		} catch (e) {
			const userFacingError = getFirstUserFacingError(e) ?? "Something went wrong, please try again later";
			toast.error(userFacingError, { title: "Failed to save clip" });
			return;
		}
		dispatchAnalyticsTrack("[AI Copilot] Created Clip", {
			fingerprint: analyticsFingerprint,
			duration: clipEndTime - clipStartTime,
		});
		toast.success("Saved to your library!");
		recordingsEventEmitter.emit("Recordings.refetch");
		onClipSaveChangeTab();

		openShareClipModal(clipId, "Send");
	}, [analyticsFingerprint, clipEndTime, clipName, clipStartTime, recordingId, onClipSaveChangeTab, saveClip]);

	// FIXME: Show a placeholder timeline until all the dependencies are ready
	return (
		<div className={cx(styles.clippingControls, { [styles.fromNote]: fromNote })}>
			<div className={styles.timelineBackground}>
				<div ref={setTimelineEl} className={styles.timelineContainer}>
					{thumbnailTrack && containerWidth !== undefined && videoRef.current && (
						<>
							<ClippingTimeline
								thumbnailTrack={thumbnailTrack}
								videoDuration={videoDuration}
								containerWidth={containerWidth}
								initialVideoPosition={videoRef.current.currentTime}
								initialVideoEndPosition={initialEndTimestamp}
								onClipStartChange={setClipStartTime}
								onClipEndChange={setClipEndTime}
								videoRef={videoRef}
								ref={timelineRef}
							/>
							<div className={styles.recordingTimeWrapper}>
								<div className={styles.recordingTimeBoundary}>{formatTimestamp(0, videoDuration)}</div>
								<div className={styles.recordingTimeBoundary}>{formatTimestamp(videoDuration, videoDuration)}</div>
							</div>
						</>
					)}
				</div>
			</div>
			<div className={styles.clipForm}>
				<div className={styles.clipDetails}>
					<h2 className={styles.clipDetailsHeading}>Start editing your clip</h2>
					<div className={styles.clipInputs}>
						<ClipTimeField
							timestamp={clipStartInput}
							onTimestampChange={onStartInputChange}
							videoDuration={videoDuration}
							error={startInputError}
							label="Start time"
						/>
						<ClipTimeField
							timestamp={clipEndInput}
							onTimestampChange={onEndInputChange}
							videoDuration={videoDuration}
							error={endInputError}
							label="End time"
						/>
						<div className={styles.clipNameWrapper}>
							<ClipNameForm name={clipName} onNameChange={onNameInputChange} />
						</div>
					</div>
				</div>
			</div>
			<div className={styles.clippingFooter}>
				<p className={styles.footerText}>
					Drag the two handles to the desired start and end points. Add a name, hit save and share!
				</p>
				<div className={styles.footerActions}>
					<Button variant="secondary" onClick={onCancel}>
						Discard
					</Button>
					<Button variant="primary" onClick={onDone} disabled={saving || !!startInputError || !!endInputError}>
						Save
					</Button>
				</div>
			</div>
		</div>
	);
}

interface ClipNameFormProps {
	name: string;
	onNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function ClipNameForm({ name, onNameChange }: ClipNameFormProps) {
	const nameFieldId = useID();

	return (
		<label htmlFor={nameFieldId}>
			<p className={styles.clipNameLabel}>Clip name</p>
			<Input
				id={nameFieldId}
				className={styles.clipNameInput}
				placeholder="Add a name for this clip"
				value={name}
				onChange={onNameChange}
				name="clip_name"
			/>
		</label>
	);
}

interface ClipTimeFieldProps {
	timestamp: string;
	onTimestampChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	videoDuration: number | undefined;
	error: string | null;
	label: string;
}

function ClipTimeField({ timestamp, onTimestampChange, label, error, videoDuration }: ClipTimeFieldProps) {
	const clipTimeFieldId = useID();

	return (
		<label htmlFor={clipTimeFieldId}>
			<p className={styles.clipNameLabel}>{label}</p>
			<Input
				id={clipTimeFieldId}
				className={styles.clipNameInput}
				placeholder={`${(videoDuration ?? 0) >= 3600 ? "01:" : ""}00:00`}
				value={timestamp}
				onChange={onTimestampChange}
				data-save-state={error ? "error" : "idle"}
				name="clip_time"
			/>
			{error && <p className={styles.clipNameError}>{error}</p>}
		</label>
	);
}

const VISIBILITY_LEVELS = ["PRIVATE", "WORKSPACE", "PUBLIC"] as const;
type Visibility = (typeof VISIBILITY_LEVELS)[number];

export function useVisibilityChangeMutation() {
	const [commit, isInFlight] = useMutationPromise<VisibilityChangeMutation>(graphql`
		mutation ClipTrimmer_VisibilityChangeMutation($clipId: ID!, $visibility: MeetingRecordingClipVisibility!)
		@raw_response_type {
			updateClipVisibility(clipId: $clipId, visibility: $visibility) {
				clip {
					id
					visibility
				}
			}
		}
	`);

	const changeVisibility = React.useCallback(
		async (clipId: string, visibility: Visibility) => {
			await commit({
				variables: { clipId, visibility },
				optimisticResponse: {
					updateClipVisibility: {
						clip: {
							id: clipId,
							visibility,
						},
					},
				},
			});
		},
		[commit],
	);

	return [changeVisibility, isInFlight] as const;
}

export function useShareClipMutation() {
	const [commit, isInFlight] = useMutationPromise(graphql`
		mutation ClipTrimmer_ShareClipMutation($clipId: ID!, $emails: [String!]!) {
			sendClipEmail(clipId: $clipId, emails: $emails) {
				ok
			}
		}
	`);

	const shareClip = React.useCallback(
		async (clipId: string, emails: readonly string[]) => {
			await commit({ variables: { clipId, emails } });
		},
		[commit],
	);

	return [shareClip, isInFlight] as const;
}
